import { useState, useEffect, useRef } from 'react';
import Session, { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { getAppsByUser } from '@/Api';
import { isApiError } from '@/services/httpClient';

/**
 * AppAdmin: Customer company administrators with limited app-specific permissions
 * - Can manage their own apps (logo, name, user management)
 * - Cannot manage user limits, model limits, or subscriptions
 * - Cannot add/remove other admins
 * - Cannot delete apps or manage platform settings
 * 
 * This hook provides AppAdmin data and permissions
 * AppAdmins are different from MasterAdmins (ROBOTOP employees)
 */
export interface AppAdminData {
  isAuthorized: boolean | null;
  userApps: any[];
  isLoading: boolean;
  error: string | null;
}

export interface UseAppAdminDataReturn {
  isAuthorized: boolean | null;
  userApps: any[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => void;
}

/**
 * Hook for AppAdmin data and permissions
 * AppAdmins can only manage their own apps with limited permissions
 * @returns AppAdmin data and utility functions
 */
export function useAppAdminData(): UseAppAdminDataReturn {
  const session = useSessionContext();
  const sessionUserId = !session.loading && session.doesSessionExist ? session.userId : '';
  const requestIdRef = useRef(0);
  const [data, setData] = useState<AppAdminData>({
    isAuthorized: null,
    userApps: [],
    isLoading: true,
    error: null,
  });

  /**
   * Check if user is authorized for AppAdmin access
   * User must have t least one app where they are admin (adminUser_ids)

   * @param userId The user ID to check
   * @returns boolean indicating if user is authorized
   */
  const checkAppAdminAuthorization = async (userId: string): Promise<boolean> => {
    const loadApps = () => getAppsByUser(userId);
    try {
      const apps = await loadApps();
      return Boolean(apps && apps.length > 0);
    } catch (error) {
      const canRefresh =
        isApiError(error) &&
        error.response.status === 401 &&
        (await Session.doesSessionExist());
      if (canRefresh && (await Session.attemptRefreshingSession())) {
        try {
          const apps = await loadApps();
          return Boolean(apps && apps.length > 0);
        } catch (retryError) {
          console.error('Error checking AppAdmin authorization:', retryError);
          return false;
        }
      }
      console.error('Error checking AppAdmin authorization:', error);
      return false;
    }
  };

  /**
   * Load AppAdmin data including user's apps, licenses, and subscriptions
   * AppAdmins can only see their own app data
   * @param userId The user ID to load data for
   */
  const loadAppAdminData = async (userId: string) => {
    const requestId = ++requestIdRef.current;
    const stillCurrent = () => requestId === requestIdRef.current;

    try {
      setData(prev => ({ ...prev, isLoading: true, error: null, isAuthorized: null }));

      // Check authorization
      const isAuthorized = await checkAppAdminAuthorization(userId);
      if (!stillCurrent()) return;
      
      if (!isAuthorized) {
        console.log('User not authorized for AppAdmin access');
        setData({
          isAuthorized: false,
          userApps: [],
          isLoading: false,
          error: null,
        });
        return;
      }

      // Load user's apps. Licenses and subscriptions are loaded by the admin page itself;
      // loading them here only delayed the permission check that gates the page.
      const userApps = await getAppsByUser(userId);
      if (!stillCurrent()) return;

      setData({
        isAuthorized: true,
        userApps,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      if (!stillCurrent()) return;
      console.error('Error loading AppAdmin data:', error);
      setData(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load AppAdmin data',
      }));
    }
  };

  /**
   * Refresh AppAdmin data
   * Useful when data needs to be reloaded after changes
   */
  const refreshData = () => {
    if (sessionUserId) {
      void loadAppAdminData(sessionUserId);
    }
  };

  useEffect(() => {
    if (session.loading) return;
    if (!sessionUserId) {
      requestIdRef.current += 1;
      setData({
        isAuthorized: false,
        userApps: [],
        isLoading: false,
        error: null,
      });
      return;
    }
    void loadAppAdminData(sessionUserId);
  }, [session.loading, sessionUserId]);

  return {
    ...data,
    refreshData,
  };
} 